import useTerminatorAdminSeason from './useTerminatorAdminSeason'
import terminatorErrorMessage from './terminatorErrorMessage'
import FootballPaymentBalances from '../Football/Admin/FootballPaymentBalances'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Button, ListGroup } from 'react-bootstrap'
import * as s from '../Eliminator/Admin/EliminatorPayments.scss'
import { getPayments, deletePaymentLogItem } from '@severed-links/common.redherrings-reducers/terminatorAdmin'
import PlayerListItem from '../Player/PlayerListItem'
import Modal from '@severed-links/common.modal'
import moment from 'moment-timezone'
import numeral from 'numeral'
import pluralize from 'pluralize'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import SeasonSelector from '../Football/SeasonSelector'
import Confirm from '@severed-links/common.confirm'

const TerminatorPayments = () => {

    const { dispatch, seasons, seasonId, setSelected } = useTerminatorAdminSeason()
    const season = seasons.find(item => item.seasonId === seasonId) || {}
    const seasonName = season.seasonName || ''
    const payments = useSelector(state => (state.terminatorAdmin.paymentLogs || []).find(item => item.seasonId === seasonId)) || { items: [] }
    const [isLoading, setLoading] = useState(false)
    const [isDeleting, setDeleting] = useState(false)
    const [error, setError] = useState('')
    const [txn, setTxn] = useState({})
    const [showDetail, setShowDetail] = useState(false)
    const [showBalances, setShowBalances] = useState(false)
    const [refresh, setRefresh] = useState(0)
    const getPaymentList = () => setRefresh(value => value + 1)
    useEffect(() => {
        setShowDetail(false)
        setShowBalances(false)
        if (!seasonId) return
        let active = true
        const load = async () => {
            setLoading(true)
            try {
                const action = await dispatch(getPayments(seasonId))
                if (action.error || action.payload?.messageType === 'error') throw new Error(action.payload?.response?.message || action.payload?.message || 'Unable to load payments.')
                if (active) setError('')
            } catch (error) { if (active) setError(terminatorErrorMessage(error)) }
            if (active) setLoading(false)
        }
        load()
        const timer = setInterval(load, 30000)
        return () => { active = false; clearInterval(timer) }
    }, [dispatch, seasonId, refresh])
    const showTransactionDetail = item => { setTxn(item); setShowDetail(true) }
    const removePayment = async paymentId => {
        if (isDeleting) return
        setDeleting(true)
        try {
            const action = await dispatch(deletePaymentLogItem({ seasonId, paymentId }))
            if (action.error || action.payload?.messageType === 'error') throw new Error(action.payload?.response?.message || action.payload?.message || 'Unable to delete payment.')
            setShowDetail(false)
            getPaymentList()
        } catch (error) { setError(terminatorErrorMessage(error)); setShowDetail(false) }
        setDeleting(false)
    }
    const deleteLogItem = removePayment
    if (!payments || !seasons) return null
    const { items } = payments
    const paymentItems = items || []
    const totalAmount = paymentItems.reduce((total, item) => total + (String(item.payment_status).toUpperCase() === 'COMPLETED' ? Number(item.payment_amount) - Number(item.refundAmount || 0) : 0), 0)
    const _detailItems = txn && showDetail ? [
        { label: 'Player', value: `${txn.firstName} ${txn.lastName}` },
        { label: 'Email', value: txn.payer_email || txn.emailAddress || 'Not recorded' },
        { label: 'Payment method', value: txn.paymentMethod || 'PayPal' },
        { label: 'Application', value: txn.applicationName || 'Terminator' },
        { label: 'Event', value: txn.item_name || `${seasonName} Terminator entry` },
        { label: 'Reference', value: txn.paymentReference || '—' },
        { label: 'Note', value: txn.paymentNote || '—' },
        { label: 'Recorded by', value: txn.recordedByName || '—' },
        { label: 'Transaction ID', value: `${txn.txn_id}` },
        { label: 'Amount', value: numeral(txn.payment_amount).format('($0,0.00)') },
        { label: 'Payment Status', value: txn.payment_status },
        { label: 'Timestamp', value: moment(txn.entryDate).format('M/D/YYYY h:mm:ss A') },
    ] : []
    return (
        <div className={s.container}>
            {error ? <Alert variant='danger'>{error}</Alert> : null}
            <section className={s.pageHeader}>
                <div className={s.headerCopy}>
                    <div className={s.eyebrow}>Terminator Admin</div>
                    <h2 className={s.title}>Payment Log</h2>
                    <div className={s.subtitle}>{seasonName} payment transactions</div>
                </div>
                <div className={s.headerActions}>
                    <Button className={s.refreshButton} variant='light' size='sm'
                        title='refresh payment log'
                        onClick={() => getPaymentList()}>
                        <FontAwesomeIcon name='rotate' spin={isLoading} />
                    </Button>
                    <SeasonSelector seasons={seasons} season={season} onSelect={name => setSelected(seasons.find(item => item.seasonName === name)?.seasonId)} />
                </div>
            </section>

            <section className={s.summaryGrid}>
                <div className={s.summaryPill}>
                    <span>{paymentItems.length}</span>
                    {pluralize('entry', paymentItems.length)}
                </div>
                <div className={s.summaryPill}>
                    <span>{numeral(totalAmount).format('($0,0.00)')}</span>
                    total recorded
                </div>
            </section>

            <FootballPaymentBalances key={seasonId} balances={payments.balances} applicationName='Terminator'
                seasonId={seasonId} onSaved={getPaymentList} />
            <section className={s.paymentCard}>
                <div className={s.listHeader}>
                    <div>Player</div>
                    <div>Amount</div>
                </div>
                <ListGroup className={s.paymentList}>
                {paymentItems.map(i =>
                <PlayerListItem key={`payment-log-item-${i.txn_id}`}
                    className={s.paymentRow}
                    player={i}
                    detail={moment(i.entryDate).format('M/D/YYYY h:mm:ss A')}
                    rightDetail={<div className={s.amount}>{numeral(i.payment_amount).format('($0,0.00)')}</div>}
                    onClick={() => showTransactionDetail(i)} />
                )}
                {!paymentItems.length ? <ListGroup.Item className={s.emptyState}>No payments located</ListGroup.Item> : null}
                </ListGroup>
            </section>
            <Modal heading='Transaction Detail'
                className={s.detailModal}
                dialogClassName={s.detailDialog}
                bodyClass={s.detailBody}
                show={showDetail}
                showFooter={false}
                onClose={() => setShowDetail(false)}>
                <div className={s.detailContainer}>
                <div className={s.detailSummary}>
                    <div className={s.detailEyebrow}>Payment</div>
                    <div className={s.detailAmount}>{numeral(txn.payment_amount).format('($0,0.00)')}</div>
                    <div className={s.detailStatus}>{txn.payment_status}</div>
                </div>
                <ListGroup className={s.detailList}>
                {_detailItems.map((i, _index) =>
                <ListGroup.Item className={s.detailItem} key={`transaction-detail-${_index}`}>
                    <div className={s.content}>
                        <div className={s.label}>{i.label}</div>
                        <div className={s.value}>{i.value}</div>
                    </div>
                </ListGroup.Item>
                )}
                </ListGroup>
                <div className={s.deleteContainer}>
                    <Confirm disabled={isDeleting} variant='danger' onConfirm={() => deleteLogItem(txn._id)}
                        heading='Delete Payment Log Item'
                        confirmText='delete log item'
                        body={'Remove this entry and recalculate the player balance? This does not issue a refund.'}
                        buttonClassName={s.deleteButton} size='sm'
                        buttonIcon='times' buttonText='Delete log item' />
                </div>
                </div>
            </Modal>
        </div>
    )
}

export default TerminatorPayments
